import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useSignup } from '../hooks/useAuth';
import { BUSINESS_TYPE_PRESETS, THEMES } from '@inboundhost/shared';
import { APP_DOMAIN } from '../lib/config';

interface SignupForm {
  email: string;
  password: string;
  businessName: string;
  businessType: string;
  subdomain: string;
}

export function SignupPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupForm>({
    defaultValues: { businessType: 'restaurant' },
  });
  const signup = useSignup();
  const navigate = useNavigate();

  const businessTypes = Object.keys(BUSINESS_TYPE_PRESETS);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form
        onSubmit={handleSubmit((data) => signup.mutate(data, { onSuccess: () => navigate('/app') }))}
        className="w-full max-w-md bg-white p-8 rounded shadow"
      >
        <h1 className="text-2xl font-bold mb-6">Create your site</h1>

        <Field label="Business name" error={errors.businessName?.message}>
          <input
            className="mt-1 w-full border rounded px-3 py-2"
            {...register('businessName', { required: 'Required' })}
          />
        </Field>

        <Field label="Business type">
          <select className="mt-1 w-full border rounded px-3 py-2" {...register('businessType', { required: true })}>
            {businessTypes.map((bt) => (
              <option key={bt} value={bt}>
                {bt}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Subdomain"
          hint="Lowercase letters, numbers, and dashes. e.g. joes-pizza"
          error={errors.subdomain?.message}
        >
          <div className="mt-1 flex items-stretch border rounded overflow-hidden">
            <input
              className="flex-1 px-3 py-2"
              {...register('subdomain', {
                required: 'Required',
                pattern: {
                  value: /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
                  message: 'Lowercase letters, digits, and dashes only',
                },
              })}
            />
            <span className="px-3 py-2 bg-gray-100 text-sm text-gray-600 border-l">.{APP_DOMAIN}</span>
          </div>
        </Field>

        <Field label="Email" error={errors.email?.message}>
          <input
            type="email"
            className="mt-1 w-full border rounded px-3 py-2"
            {...register('email', { required: 'Required' })}
          />
        </Field>

        <Field label="Password" error={errors.password?.message}>
          <input
            type="password"
            className="mt-1 w-full border rounded px-3 py-2"
            {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 chars' } })}
          />
        </Field>

        <p className="text-xs text-gray-500 mb-4">
          You'll get the {THEMES[0].label} theme by default — you can change this later.
        </p>

        {signup.error && <div className="mb-3 text-sm text-red-600">{(signup.error as Error).message}</div>}

        <button
          type="submit"
          disabled={signup.isPending}
          className="w-full bg-gray-900 text-white py-2 rounded hover:bg-gray-800 disabled:opacity-50"
        >
          {signup.isPending ? 'Creating site…' : 'Create site'}
        </button>

        <p className="text-sm text-gray-600 mt-4">
          Have an account?{' '}
          <Link to="/login" className="underline">
            Log in
          </Link>
        </p>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block mb-3">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
      {error && <span className="block text-xs text-red-600 mt-1">{error}</span>}
    </label>
  );
}
